/**
 * DischargePlanningSoftwarePage — /discharge-planning-software (iter 179).
 *
 * The public "For Hospitals" landing page. Recomposed around the same
 * MATCH · PLACE · CONNECT · MEASURE pillars the homepage uses, so the
 * hero CTA lands on a page that speaks the same language.
 *
 * IP-protection rules apply the same as the homepage:
 *   - No numbered sequence of internal steps.
 *   - No mechanism/algorithm/scoring reveal.
 *   - No fabricated percentages or minutes.
 *   - HIPAA-aligned language, never "HIPAA compliant" / "SOC 2 certified".
 *
 * Sells the OUTCOME for hospital case-management leadership.
 * Protects the engine completely.
 */
import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import {
  ArrowRight, ArrowUpRight, ArrowDown, ShieldCheck, BadgeCheck, Lock,
  Hospital, CheckCircle2, Clock, Eye, LineChart, Sparkles, Layers,
  Compass, MapPin, Radio, Gauge,
} from 'lucide-react';
import { PublicNavbar } from '../components/brand/PublicNavbar';
import { Footer } from '../components/brand/Footer';
import { Seo } from '../components/seo/Seo';
import {
  organizationSchema, websiteSchema, breadcrumbSchema,
  softwareApplicationSchema,
} from '../lib/seoSchemas';
import { InstitutionInquiryModal } from '../components/brand/InstitutionInquiryModal';

const PILLARS = [
  { key: 'match',   icon: Compass, label: 'MATCH',   body: 'Appropriate post-acute options for each patient.' },
  { key: 'place',   icon: MapPin,  label: 'PLACE',   body: 'A clearer path to confirmed placement.' },
  { key: 'connect', icon: Radio,   label: 'CONNECT', body: 'Visibility across the transition — end-to-end.' },
  { key: 'measure', icon: Gauge,   label: 'MEASURE', body: 'Actionable transition performance for leadership.' },
];

const HOSPITAL_OUTCOMES = [
  {
    key: 'faster_placement',
    icon: Clock,
    title: 'Faster placement',
    body: 'Less time between the patient becoming ready and confirmed acceptance from a receiving provider.',
  },
  {
    key: 'fewer_manual_touches',
    icon: LineChart,
    title: 'Fewer manual touches',
    body: 'Case-management teams spend less time calling, faxing, searching, and chasing status.',
  },
  {
    key: 'greater_visibility',
    icon: Eye,
    title: 'Greater visibility',
    body: 'Leadership can see whether the patient was accepted and when post-acute care is expected to begin.',
  },
  {
    key: 'measurable_performance',
    icon: BadgeCheck,
    title: 'Measurable performance',
    body: 'Transition metrics reported by service line, unit, and level of care — pilot-verified before publish.',
  },
];

const TRUST_PILLARS = [
  {
    icon: ShieldCheck,
    title: 'PHI on covered paths only',
    body: 'Patient data only touches services behind signed agreements. Non-covered fallbacks are refused, not silently used.',
  },
  {
    icon: Lock,
    title: 'Authorized, audited access',
    body: 'Every view of a patient record is permission-scoped and written to an immutable audit log.',
  },
  {
    icon: BadgeCheck,
    title: 'Verified providers',
    body: 'CMS Care Compare imports, license and exclusion screening, and provider verification.',
  },
  {
    icon: Layers,
    title: 'Role-scoped workflows',
    body: 'Case management, providers, and families each see the transition through their own scoped surface.',
  },
];

export default function DischargePlanningSoftwarePage() {
  const [modal, setModal] = useState({ open: false, interest: 'demo' });
  const openDemo  = () => setModal({ open: true, interest: 'demo' });
  const openPilot = () => setModal({ open: true, interest: 'pilot' });

  const trail = [
    { name: 'Home', path: '/' },
    { name: 'For Hospitals', path: '/discharge-planning-software' },
  ];

  return (
    <div className="min-h-screen bg-cn-bg text-cn-text">
      <Seo
        title="CaraLoom for Hospitals | Post-Acute Placement & Care Transitions"
        description="For hospital case-management leadership: move patients toward appropriate post-acute placement — with visibility across the transition. Close the transition, not just the referral."
        path="/discharge-planning-software"
        keywords={[
          'discharge planning software',
          'hospital case management',
          'post-acute placement',
          'care transitions for hospitals',
          'hospital referral management',
          'post-acute network management',
        ]}
        schema={[
          organizationSchema(),
          websiteSchema(),
          breadcrumbSchema(trail),
          softwareApplicationSchema({
            description: 'CaraLoom for hospitals — the connective layer between the hospital and post-acute care. Move patients toward appropriate placement while maintaining visibility across the transition.',
            slug: 'discharge-planning-software',
            featureList: [
              'Post-acute placement for hospital case-management teams',
              'Care transition visibility from referral to start of care',
              'Transition performance insight for case-management leadership',
              'Enterprise security posture for protected health information',
            ],
          }),
        ]}
      />
      <PublicNavbar />

      <Hero onDemo={openDemo} onPilot={openPilot} />
      <PatientJourney />
      <FourPillars />
      <VisibilityCard />
      <HospitalOutcomes />
      <PilotBand onDemo={openDemo} onPilot={openPilot} />
      <TrustSection />
      <FinalCTA onDemo={openDemo} onPilot={openPilot} />

      <Footer />

      <InstitutionInquiryModal
        open={modal.open}
        defaultInterest={modal.interest}
        onOpenChange={(v) => setModal((s) => ({ ...s, open: v }))}
        source="hospitals_landing"
      />
    </div>
  );
}

/* ─────────────────────────── Hero ─────────────────────────── */

function Hero({ onDemo, onPilot }) {
  return (
    <section className="relative overflow-hidden" data-testid="hospitals-hero">
      <div className="cn-container pt-20 pb-16 md:pt-28 md:pb-24">
        <div className="grid lg:grid-cols-[1.15fr_.85fr] gap-12 lg:gap-16 items-center">
          <div>
            <div
              className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-cn-bone-200/80 dark:bg-white/5 border border-cn-border mb-6 animate-fade-in-up"
              data-testid="hospitals-hero-eyebrow"
            >
              <Hospital className="w-3.5 h-3.5 text-cn-primary dark:text-cn-accent" />
              <span className="text-xs font-semibold tracking-wide uppercase text-cn-text">
                For Hospitals &amp; Health-System Leadership
              </span>
            </div>

            <h1
              className="cn-display text-[2.5rem] sm:text-5xl lg:text-6xl text-cn-text leading-[1.04] tracking-tight animate-fade-in-up stagger-1"
              data-testid="hospitals-hero-headline"
            >
              Move patients toward{' '}
              <span className="italic font-medium text-cn-primary dark:text-cn-accent">appropriate placement</span>
              {' '}— with visibility across the transition.
            </h1>

            <p
              className="mt-6 text-lg md:text-xl text-cn-text-muted leading-relaxed max-w-xl animate-fade-in-up stagger-2"
              data-testid="hospitals-hero-sub"
            >
              CaraLoom is the connective layer between the hospital and post-acute care. Case-management
              leadership sees where every discharge stands — from the moment a patient is ready
              through the start of care.
            </p>

            <div className="mt-10 flex flex-wrap gap-3 animate-fade-in-up stagger-3">
              <button type="button" onClick={onDemo} className="cn-btn cn-btn-primary text-base font-bold"
                data-testid="hospitals-cta-demo">
                See CaraLoom in Action <ArrowRight className="w-4 h-4" />
              </button>
              <button type="button" onClick={onPilot}
                className="cn-btn bg-white dark:bg-cn-ink-800 text-cn-text border border-cn-border hover:bg-cn-bone-100 dark:hover:bg-cn-ink-700 text-base font-bold"
                data-testid="hospitals-cta-pilot">
                Start a Hospital Pilot <ArrowUpRight className="w-4 h-4" />
              </button>
            </div>
          </div>

          <div className="flex justify-center lg:justify-end animate-fade-in-up stagger-3">
            <HeroCard />
          </div>
        </div>
      </div>
      <div className="pointer-events-none absolute -top-40 left-1/2 -translate-x-1/2 w-[680px] h-[480px] rounded-full bg-cn-sage-200/30 blur-3xl animate-cn-aurora" />
    </section>
  );
}

function HeroCard() {
  return (
    <div
      className="relative w-full max-w-[540px] rounded-3xl overflow-hidden border border-cn-border shadow-2xl bg-cn-primary text-white"
      data-testid="hospitals-hero-card"
    >
      <div className="absolute inset-0 opacity-25 pointer-events-none bg-gradient-to-br from-cn-accent/40 via-transparent to-cn-secondary/40" aria-hidden />
      <div className="relative p-8 md:p-10 aspect-[16/11] flex flex-col justify-between">
        <div>
          <div className="text-[10px] uppercase tracking-widest text-white/70 font-mono">
            Case Management · overview
          </div>
          <div className="mt-3 cn-display text-2xl text-white leading-snug">
            Every discharge, visible.
          </div>
        </div>

        <div className="grid grid-cols-2 gap-3 text-white">
          {[
            { label: 'Appropriate options',   icon: CheckCircle2 },
            { label: 'Confirmed placement',   icon: CheckCircle2 },
            { label: 'Coverage verified',     icon: CheckCircle2 },
            { label: 'Start of care in sight',icon: CheckCircle2 },
          ].map((o) => (
            <div key={o.label} className="flex items-center gap-2 rounded-xl bg-white/10 backdrop-blur-sm px-3 py-2 border border-white/15">
              <o.icon className="w-4 h-4 text-cn-accent flex-none" strokeWidth={2} />
              <span className="text-xs">{o.label}</span>
            </div>
          ))}
        </div>

        <div className="text-[11px] text-white/70 italic">
          Illustrative only. Detailed views shown in enterprise product demonstrations.
        </div>
      </div>
    </div>
  );
}

/* ───────────── Patient journey (the only public journey visual) ───── */

function PatientJourney() {
  const stops = [
    { key: 'hospital',   icon: Hospital,     title: 'Hospital',           body: 'Patient needs post-acute care.' },
    { key: 'caraloom',   icon: Sparkles,     title: 'CaraLoom',           body: 'Intelligent post-acute coordination.', emphasized: true },
    { key: 'post-acute', icon: CheckCircle2, title: 'Post-Acute Care',    body: 'The next level of care begins.' },
  ];
  return (
    <section className="py-20 md:py-28 border-t border-cn-border bg-cn-bg" data-testid="hospitals-journey">
      <div className="cn-container">
        <div className="max-w-3xl mx-auto text-center">
          <div className="cn-eyebrow mb-3">Where CaraLoom sits</div>
          <h2 className="cn-display text-3xl md:text-5xl text-cn-text leading-[1.06] tracking-tight">
            The connective layer between{' '}
            <span className="italic text-cn-text-muted">the hospital and post-acute care.</span>
          </h2>
        </div>

        <div className="mt-14 hidden md:grid grid-cols-3 gap-6 items-stretch">
          {stops.map((s) => (
            <div
              key={s.key}
              className={`rounded-3xl border p-8 text-center flex flex-col items-center ${
                s.emphasized
                  ? 'border-cn-primary/40 dark:border-cn-accent/60 bg-white dark:bg-cn-ink-800 shadow-lg'
                  : 'border-cn-border bg-cn-bone-100/60 dark:bg-cn-ink-900/40'
              }`}
              data-testid={`hospitals-journey-${s.key}`}
            >
              <div className={`w-14 h-14 rounded-2xl flex items-center justify-center ${
                s.emphasized
                  ? 'bg-cn-primary/10 dark:bg-cn-accent/15 text-cn-primary dark:text-cn-accent'
                  : 'bg-cn-bone-200 dark:bg-white/5 text-cn-text-muted'
              }`}>
                <s.icon className="w-7 h-7" strokeWidth={1.6} />
              </div>
              <h3 className="mt-5 cn-display text-xl text-cn-text">{s.title}</h3>
              <p className="mt-2 text-sm text-cn-text-muted leading-relaxed max-w-[22ch]">{s.body}</p>
            </div>
          ))}
        </div>

        <ol className="mt-12 md:hidden space-y-4">
          {stops.map((s, i) => (
            <li key={s.key}>
              <div className={`rounded-2xl border p-5 flex items-start gap-4 ${
                s.emphasized
                  ? 'border-cn-primary/40 dark:border-cn-accent/60 bg-white dark:bg-cn-ink-800'
                  : 'border-cn-border bg-cn-bone-100/60 dark:bg-cn-ink-900/40'
              }`}>
                <div className="flex-none w-11 h-11 rounded-xl bg-cn-primary/10 dark:bg-cn-accent/15 text-cn-primary dark:text-cn-accent flex items-center justify-center">
                  <s.icon className="w-5 h-5" strokeWidth={1.7} />
                </div>
                <div>
                  <h3 className="cn-display text-base font-semibold text-cn-text">{s.title}</h3>
                  <p className="mt-1 text-sm text-cn-text-muted leading-relaxed">{s.body}</p>
                </div>
              </div>
              {i < stops.length - 1 && (
                <div className="flex justify-center py-2" aria-hidden>
                  <ArrowDown className="w-4 h-4 text-cn-text-muted opacity-50" />
                </div>
              )}
            </li>
          ))}
        </ol>
      </div>
    </section>
  );
}

/* ─────────────── Four independent pillars (same as homepage) ─────── */

function FourPillars() {
  return (
    <section className="py-24 md:py-32 bg-cn-bone-100 dark:bg-cn-ink-900 border-y border-cn-border" data-testid="hospitals-pillars">
      <div className="cn-container">
        <div className="max-w-3xl">
          <div className="cn-eyebrow mb-3">CaraLoom for hospitals</div>
          <h2 className="cn-display text-4xl md:text-5xl text-cn-text leading-[1.05] tracking-tight">
            Four outcomes that characterize the platform.
          </h2>
          <p className="mt-5 text-cn-text-muted leading-relaxed max-w-2xl">
            Not a process. Four independent capabilities that together define what your
            case-management team gets when they run transitions on CaraLoom.
          </p>
        </div>

        <div className="mt-14 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 lg:gap-6">
          {PILLARS.map((p) => (
            <div
              key={p.key}
              className="rounded-2xl border border-cn-border bg-cn-bg p-6 lg:p-7"
              data-testid={`hospitals-pillar-${p.key}`}
            >
              <div className="w-11 h-11 rounded-2xl bg-cn-primary/10 dark:bg-cn-accent/15 text-cn-primary dark:text-cn-accent flex items-center justify-center">
                <p.icon className="w-5 h-5" strokeWidth={1.8} />
              </div>
              <h3
                className="mt-6 cn-display text-xl text-cn-text tracking-tight"
                data-testid={`hospitals-pillar-${p.key}-label`}
              >
                {p.label}
              </h3>
              <p className="mt-2 text-sm text-cn-text-muted leading-relaxed">{p.body}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ───────────── Visibility statement (outcome only) ───────────── */

function VisibilityCard() {
  return (
    <section className="py-24 md:py-32 bg-cn-bg" data-testid="hospitals-visibility">
      <div className="cn-container grid lg:grid-cols-[1.1fr_.9fr] gap-12 lg:gap-16 items-center">
        <div>
          <div className="cn-eyebrow mb-3">Transition visibility</div>
          <h2 className="cn-display text-4xl md:text-5xl text-cn-text leading-[1.04] tracking-tight">
            Know where every discharge stands.
          </h2>
          <p className="mt-5 cn-display text-xl md:text-2xl text-cn-primary dark:text-cn-accent italic">
            Sent isn&apos;t enough.
          </p>
          <p className="mt-5 text-lg text-cn-text-muted leading-relaxed max-w-xl">
            From referral to first visit, the transition stays visible — so case-management
            leadership knows what&apos;s in flight, what&apos;s at risk, and what has landed.
          </p>
        </div>

        <div className="rounded-3xl border border-cn-border bg-cn-bone-100/70 dark:bg-cn-ink-900/50 p-6 md:p-8 shadow-sm">
          <div className="text-[11px] uppercase tracking-widest text-cn-text-muted">
            Transition · overview
          </div>
          <div className="mt-3 flex items-center gap-2">
            <span className="w-2 h-2 rounded-full bg-cn-accent animate-pulse" aria-hidden />
            <span className="cn-display text-xl text-cn-text">On track</span>
          </div>
          <ul className="mt-6 space-y-3">
            {['Appropriate options', 'Confirmed placement', 'Coverage verified', 'Start of care in sight'].map((label) => (
              <li key={label} className="flex items-center gap-3">
                <CheckCircle2 className="w-4 h-4 flex-none text-cn-primary dark:text-cn-accent" strokeWidth={2} />
                <span className="text-sm text-cn-text">{label}</span>
              </li>
            ))}
          </ul>
          <p className="mt-6 text-[11px] text-cn-text-muted italic">
            Illustrative outcomes only. Detailed views shown in enterprise product demonstrations.
          </p>
        </div>
      </div>
    </section>
  );
}

/* ─────────────────── Hospital outcomes (qualitative + optional verified) ── */

function HospitalOutcomes() {
  const [metrics, setMetrics] = useState({});
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const { data } = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/public/roi-metrics`);
        if (!alive || !data?.published) return;
        const byKey = {};
        (data.cards || []).forEach((c) => { if (c.value) byKey[c.key] = c; });
        setMetrics(byKey);
      } catch { /* silent */ }
    })();
    return () => { alive = false; };
  }, []);

  return (
    <section className="py-24 md:py-32 bg-cn-bone-100 dark:bg-cn-ink-900 border-y border-cn-border" data-testid="hospitals-outcomes">
      <div className="cn-container">
        <div className="max-w-3xl">
          <div className="cn-eyebrow mb-3">For hospital leadership</div>
          <h2 className="cn-display text-4xl md:text-5xl text-cn-text leading-[1.05] tracking-tight">
            Better transitions aren&apos;t just better care.{' '}
            <span className="italic text-cn-text-muted">They&apos;re better operations.</span>
          </h2>
        </div>

        <div className="mt-14 grid md:grid-cols-2 lg:grid-cols-4 gap-4 lg:gap-6">
          {HOSPITAL_OUTCOMES.map((r) => {
            const m = metrics[r.key];
            return (
              <div key={r.title} className="rounded-2xl border border-cn-border bg-cn-bg p-6">
                <div className="w-10 h-10 rounded-xl bg-cn-primary/10 dark:bg-cn-accent/15 text-cn-primary dark:text-cn-accent flex items-center justify-center">
                  <r.icon className="w-5 h-5" strokeWidth={1.8} />
                </div>
                <h3 className="mt-5 cn-display text-lg text-cn-text">{r.title}</h3>
                <p className="mt-2 text-sm text-cn-text-muted leading-relaxed">{r.body}</p>
                {m && (
                  <div
                    className="mt-4 pt-4 border-t border-cn-border"
                    data-testid={`hospitals-roi-metric-${r.key}`}
                  >
                    <div className="cn-display text-2xl text-cn-primary dark:text-cn-accent tabular-nums">{m.value}</div>
                    <div className="text-xs text-cn-text-muted">{m.caption}</div>
                  </div>
                )}
              </div>
            );
          })}
        </div>

        <p className="mt-8 text-xs text-cn-text-muted italic max-w-2xl">
          Outcome metrics are reported from verified pilots — not published until they reflect your
          actual results.
        </p>
      </div>
    </section>
  );
}

/* ──────────────── Pilot band ─────────── */

function PilotBand({ onPilot, onDemo }) {
  const focus = ['Placement outcomes', 'Transition visibility', 'Team time recovered', 'Time to start of care'];
  return (
    <section className="py-24 md:py-32 bg-cn-bg" data-testid="hospitals-pilot">
      <div className="cn-container grid lg:grid-cols-[1.1fr_.9fr] gap-12 lg:gap-16 items-start">
        <div>
          <div className="cn-eyebrow mb-3">Hospital pilot</div>
          <h2 className="cn-display text-4xl md:text-5xl text-cn-text leading-[1.05] tracking-tight">
            Start with one unit.{' '}
            <span className="italic text-cn-text-muted">Measure one transition.</span>
          </h2>
          <p className="mt-5 text-cn-text-muted leading-relaxed max-w-xl">
            Launch CaraLoom with a focused hospital pilot and measure what matters for your service
            line, your case-management team, and your patients.
          </p>
          <div className="mt-10 flex flex-wrap gap-3">
            <button type="button" onClick={onPilot} className="cn-btn cn-btn-primary text-base font-bold"
              data-testid="hospitals-pilot-cta-primary">
              Start a Hospital Pilot <ArrowRight className="w-4 h-4" />
            </button>
            <button type="button" onClick={onDemo}
              className="cn-btn bg-white dark:bg-cn-ink-800 text-cn-text border border-cn-border hover:bg-cn-bone-100 dark:hover:bg-cn-ink-700 text-base"
              data-testid="hospitals-pilot-cta-secondary">
              Schedule a Demo <ArrowUpRight className="w-4 h-4" />
            </button>
          </div>
        </div>

        <ul className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {focus.map((m) => (
            <li key={m} className="rounded-xl border border-cn-border bg-cn-bone-100/60 dark:bg-cn-ink-900/40 px-4 py-3 flex items-center gap-3">
              <span className="w-1.5 h-1.5 rounded-full bg-cn-accent" aria-hidden />
              <span className="text-sm text-cn-text">{m}</span>
            </li>
          ))}
        </ul>
      </div>
    </section>
  );
}

/* ─────────────────── Trust ─────────────── */

function TrustSection() {
  return (
    <section className="py-24 md:py-32 bg-cn-bone-100 dark:bg-cn-ink-900 border-y border-cn-border" data-testid="hospitals-trust">
      <div className="cn-container grid lg:grid-cols-[1.1fr_.9fr] gap-12 lg:gap-16 items-start">
        <div>
          <div className="cn-eyebrow mb-3">Enterprise readiness</div>
          <h2 className="cn-display text-4xl md:text-5xl text-cn-text leading-[1.05] tracking-tight">
            Built for the security review.
          </h2>
          <p className="mt-5 text-cn-text-muted leading-relaxed max-w-lg">
            Trust is more important than aggressive marketing language. CaraLoom is designed with
            HIPAA-aligned safeguards for protected health information, immutable audit trails, and
            role-scoped workflows.
          </p>
        </div>

        <ul className="space-y-6">
          {TRUST_PILLARS.map((t) => (
            <li key={t.title} className="flex gap-4 items-start">
              <div className="flex-none w-11 h-11 rounded-full bg-cn-primary/10 dark:bg-cn-accent/10 text-cn-primary dark:text-cn-accent flex items-center justify-center">
                <t.icon className="w-5 h-5" strokeWidth={1.8} />
              </div>
              <div>
                <h4 className="cn-display text-base font-semibold text-cn-text leading-snug">{t.title}</h4>
                <p className="text-sm text-cn-text-muted leading-relaxed mt-1">{t.body}</p>
              </div>
            </li>
          ))}
        </ul>
      </div>
    </section>
  );
}

/* ─────────────── Final CTA ───────────── */

function FinalCTA({ onDemo, onPilot }) {
  return (
    <section className="py-24 md:py-32 bg-cn-primary text-white" data-testid="hospitals-final-cta">
      <div className="cn-container text-center">
        <h2 className="cn-display text-4xl md:text-5xl lg:text-6xl font-bold leading-[1.04] tracking-tight max-w-3xl mx-auto !text-white">
          The referral shouldn&apos;t be{' '}
          <em className="not-italic text-cn-accent">where visibility ends.</em>
        </h2>
        <p className="mt-5 text-base md:text-lg text-white/85 leading-relaxed max-w-xl mx-auto">
          See how CaraLoom can help your case-management team create a connected path from
          discharge to the start of post-acute care.
        </p>
        <div className="mt-10 flex flex-wrap justify-center gap-3">
          <button type="button" onClick={onDemo} className="cn-btn cn-btn-accent text-base font-bold"
            data-testid="hospitals-final-cta-demo">
            See CaraLoom in Action <ArrowRight className="w-4 h-4" />
          </button>
          <button type="button" onClick={onPilot}
            className="cn-btn bg-white text-cn-primary hover:bg-white/95 text-base font-bold"
            data-testid="hospitals-final-cta-pilot">
            Start a Hospital Pilot <ArrowUpRight className="w-4 h-4" />
          </button>
        </div>
      </div>
    </section>
  );
}
